import React, { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Input } from "@design-system/Input/Input";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useCreateWorkspaceEmailAlias } from "@api/workspaceEmailAlias.api";
import { didRequestReachServer } from "@api/apiError";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { WorkspaceEmailAliasKind } from "@common/constants/domains/receipts/constants";
import { getWorkspaceEmailAliasDomain } from "@common/helpers/workspace-email-alias";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./AddEmailAliasModal.module.scss";

function getAddEmailAliasFormSchema(kind: WorkspaceEmailAliasKind) {
    const domain = getWorkspaceEmailAliasDomain(kind);
    return z.object({
        emailLocalPart: z
            .string()
            .trim()
            .min(1, "Email address is required")
            .refine(
                (value) =>
                    z.string().email().safeParse(`${value}@${domain}`).success,
                "Invalid email address",
            ),
        counterpartyId:
            kind === "receipts"
                ? z.string().uuid().nullable()
                : z.string().uuid({ message: "Select a counterparty" }),
    });
}

interface AddEmailAliasFormValues {
    emailLocalPart: string;
    counterpartyId: string | null;
}

interface AddEmailAliasModalProps {
    show: boolean;
    onClose: () => void;
    kind: WorkspaceEmailAliasKind;
}

export const AddEmailAliasModal: React.FC<AddEmailAliasModalProps> = ({
    show,
    onClose,
    kind,
}) => {
    const createEmailAlias = useCreateWorkspaceEmailAlias();
    const { toast, error } = useToaster();
    const [selectedCounterparty, setSelectedCounterparty] = useState<
        CounterpartyComboboxValue | undefined
    >();
    const domain = getWorkspaceEmailAliasDomain(kind);
    const schema = getAddEmailAliasFormSchema(kind);

    const formik = useFormik<AddEmailAliasFormValues>({
        initialValues: {
            emailLocalPart: "",
            counterpartyId: null,
        },
        validate: validateFormikWithZodSchema(schema),
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: submitHelper({
            handler: async ({ emailLocalPart, counterpartyId }) => {
                await createEmailAlias.mutateAsync({
                    emailLocalPart: emailLocalPart.trim(),
                    kind,
                    counterpartyId,
                });
                toast("Email alias created");
                handleClose();
            },
            failureHandler: async (requestError) => {
                if (!didRequestReachServer(requestError)) {
                    error("Failed to create email alias");
                }
            },
        }),
    });

    const handleClose = () => {
        formik.resetForm();
        setSelectedCounterparty(undefined);
        onClose();
    };

    return (
        <PlainModal show={show} onHide={handleClose}>
            <StandardModalHeader>Add Email Alias</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <FormikField<string>
                            name="emailLocalPart"
                            label="Email"
                        >
                            {({ field, invalid }) => (
                                <div className={styles.fieldWrapper}>
                                    <Input
                                        {...field}
                                        placeholder="myalias"
                                        type="text"
                                        size="xl"
                                        className={styles.field}
                                        invalid={invalid}
                                    />
                                    <span>@{domain}</span>
                                </div>
                            )}
                        </FormikField>

                        {kind !== "receipts" && (
                            <FormikField
                                name="counterpartyId"
                                label="Counterparty"
                                control="button"
                            >
                                <CounterpartyCombobox
                                    selectedCounterparty={selectedCounterparty}
                                    onChange={(counterparty) => {
                                        setSelectedCounterparty(counterparty);
                                        void formik.setFieldValue(
                                            "counterpartyId",
                                            counterparty?.id ?? null,
                                        );
                                    }}
                                    placeholder="Select counterparty"
                                    searchPlaceholder="Search counterparties..."
                                    createButtonText="Create counterparty"
                                    emptyStateText="No matching counterparties"
                                />
                            </FormikField>
                        )}

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={handleClose}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting}
                                type="submit"
                            >
                                Save
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
